/**
 * Servicio centralizado para Ajustes Detalles (Payment Details).
 * Consulta facturas con detalles de pago y verifica flags de activación.
 * Sigue el patrón de PagosService.
 * Fecha: 01/10/2026
 */
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { InvoicePD, TipoRegistroPago } from '../../models/payment-detail.model';
import { ParametrosGeneralesService } from '../parametrosgenerales/parametrosgenerales.service';

@Injectable()
export class PaymentDetailsService {

  constructor(
    private http: HttpClient,
    private parametrosService: ParametrosGeneralesService
  ) { }

  /**
   * Obtiene las facturas con detalles de pago desde el endpoint.
   * URL_SERVICIOS ya incluye /api/v1, no se duplica el prefijo.
   * AuthInterceptor inyecta el token automáticamente.
   * Fecha: 01/10/2026
   *
   * @param contributorOriginId - ID del contribuyente origen
   * @param contributorDestinationId - ID del contribuyente destino
   * @param tipoRegistroPago - tipo de registro de pago
   * @param fechaConsulta - fecha de consulta opcional (formato YYYY-MM-DD)
   * @param funderId - ID del fondeador, requerido solo para COBRANZA FONDEO (pago a fondeador). Opcional. [22/10/2026]
   * @param currency - moneda (PESOS/DOLARES), requerida solo para COBRANZA FONDEO. Opcional. [22/10/2026]
   */
  getInvoicesPD(
    contributorOriginId: number,
    contributorDestinationId: number,
    tipoRegistroPago: TipoRegistroPago,
    fechaConsulta?: string,
    funderId?: number,
    currency?: string
  ): Observable<InvoicePD[]> {

    const url = `${environment.URL_SERVICIOS}/reports/get_invoices_pd`;

    let params = new HttpParams()
      .set('contributor_origin_id', contributorOriginId.toString())
      .set('contributor_destination_id', contributorDestinationId.toString())
      .set('tipo_registro_pago', tipoRegistroPago);

    if (fechaConsulta) {
      params = params.set('fecha_consulta', fechaConsulta);
    }

    // [22/10/2026] funder_id y currency solo se envian cuando vienen definidos (pantalla pago a fondeador).
    // Las demas pantallas que no los pasan conservan el comportamiento previo.
    if (funderId !== undefined && funderId !== null && !isNaN(Number(funderId))) {
      params = params.set('funder_id', funderId.toString());
    }
    if (currency) {
      params = params.set('currency', currency);
    }

    return this.http.get<InvoicePD[]>(url, { params });
  }

  /**
   * Formatea un monto numérico como string con comas para miles.
   * Acepta number (backend) o string (fallback). Compatible con getTotal().
   * Fecha: 01/10/2026
   *
   * @param valor - monto a formatear (number o string)
   */
  formatearMonto(valor: number | string): string {
    const num = (typeof valor === 'number') ? valor : parseFloat((valor || '0').toString().replace(/,/g, ''));
    if (isNaN(num)) { return '0.00'; }
    return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /**
   * Determina si un error HTTP 422 del endpoint PD corresponde a flag inactivo
   * en backend. Retorna true si el error indica flag desactivado (fallback a legacy).
   * Fecha: 01/10/2026
   *
   * @param err - error HTTP capturado
   */
  esFlagInactivo(err: any): boolean {
    if (!err || err.status !== 422) { return false; }
    const body = err.error;
    if (!body) { return false; }
    const mensaje = (body.message || body.error || '').toLowerCase();
    return mensaje.includes('flag') || mensaje.includes('inactive') || mensaje.includes('inactiv');
  }

  /**
   * Verifica si un flag de ajustes detalles está activo en parámetros generales.
   * Retorna true si resp['value'] === 'SI', false en cualquier otro caso o error.
   * Fecha: 01/10/2026
   *
   * @param flagName - nombre del flag a consultar (ej: 'AJUSTE_DETALLE_PAGO_PROVEEDOR')
   */
  checkFlag(flagName: string): Observable<boolean> {
    return this.parametrosService.getGeneralParam(flagName).pipe(
      map((resp: any) => {
        return resp['value'] === 'SI';
      }),
      catchError(() => of(false))
    );
  }
}
